/**
 * Strings for the user-management P1 screens: search, moderation, the audit
 * log, invites, nicknames, pokes, message requests, ignore, the recovery
 * ladder, the new-account period, ownership transfer, recently deleted, the
 * moderator brake and Settings > Server.
 *
 * Keys without the `um.` prefix are user-management §2.11, §3.8 and §4.10
 * word for word. `um.*` keys are ours, for labels the spec does not word (it
 * lists options in one line, for one).
 */
import type { Dict } from './core';

export const umEn: Dict = {
  // ---- spec §3.8: block and ignore, message requests
  'ignore.action': 'Ignore',
  'ignore.confirm.title': 'Ignore {name}?',
  'ignore.confirm.body':
    "Their messages will be hidden and their notifications silenced. You stay friends and can still message each other. They won't be told.",
  'ignore.undo': 'Stop ignoring',
  'ignore.message': 'Message from someone you ignore · Show',
  'requests.title': 'Message Requests',
  'requests.preview': "Media is hidden and links don't open until you accept.",
  'requests.accept': 'Accept',
  'requests.dismiss': 'Dismiss',
  'requests.report': 'Report',
  'requests.spam': 'Spam',
  'requests.empty': 'No message requests.',
  // ---- spec §2.11: recovery, new-account period
  'recovery.title': 'Get back into your account',
  'recovery.device': "Approve from a device where you're signed in",
  'recovery.wait':
    "For your safety, this finishes on {date} at {time}. We've told all your devices; you can cancel it from any of them.",
  'recovery.alert':
    "Someone asked to get into your account using {method}. If this wasn't you, cancel it now.",
  'recovery.cancel': 'Cancel this request',
  'recovery.signedOut':
    "You're back in. We signed out every other device, and your email can't be changed until {date}.",
  'recovery.lost':
    "If you only forgot your password, you can reset it from your email. If two-step verification is on and you have neither a recovery code nor a signed-in device, the Neosocial support team can turn two-step off for you once they have checked that it's really you.",
  // ---- spec §4.10: invites
  'invite.title': 'Invite people to {server}',
  'invite.temporary': 'Temporary membership: removed when they disconnect, unless they get a role',
  'invite.copy': 'Copy link',
  'invite.invalid': 'This invite is invalid or has expired.',
  // ---- spec §4.10: roles, ownership
  'roles.cantMove': 'You can move only roles below your highest role, and only to places below it.',
  'owner.transfer': 'Transfer ownership',
  'owner.transfer.body':
    '{name} becomes the owner of {server} right away, and you stop being the owner. You, the Admins and {name} are told.',
  'owner.transfer.code': 'You will be asked for a code from your authenticator app.',
  // ---- spec §4.10: moderation
  'mod.timeout': 'Time out',
  'mod.timeout.notice':
    'A moderator timed you out in {server} until {time}. You can still read. Reason: {reason}',
  'mod.kick': 'Kick from server',
  'mod.kick.notice':
    'A moderator removed you from {server}. You can rejoin with a new invite. Reason: {reason}',
  'mod.ban': 'Ban',
  'mod.ban.notice': 'A moderator banned you from {server} until {time}. Reason: {reason}',
  'mod.ban.noticePerm': 'A moderator banned you from {server}. Reason: {reason}',
  'mod.serverMute': 'Server mute',
  'mod.serverDeafen': 'Server deafen',
  'mod.move': 'Move to…',
  'mod.disconnect': 'Disconnect from voice',
  'mod.reason': 'Reason (they and other moderators will see it)',
  'mod.noReason': 'No reason given',
  'mod.needMfa':
    'This server requires two-step verification for moderators. Turn it on to continue.',
  'mod.cantHigher':
    "You can't do this to someone whose highest role is the same as or above yours.",
  'mod.brake':
    "That's many actions in a short time. Enter your two-step code to continue. The owner and the Admins have been told.",
  'mod.brake.destructive':
    'Deleting channels or roles, giving out Administrator or a Manage permission, bulk-deleting messages and creating webhooks need a fresh two-step code. The owner and the Admins will be told.',
  'restore.title': 'Recently deleted',
  'restore.row': 'Deleted by {actor} on {date}. You can restore it until {until}.',
  'restore.action': 'Restore',
  // ---- spec §4.10: audit log lines
  'audit.kick': '{actor} kicked {target}',
  'audit.ban': '{actor} banned {target} until {time}',
  'audit.banPerm': '{actor} banned {target}',
  'audit.unban': '{actor} unbanned {target}',
  'audit.timeout': '{actor} timed out {target} until {time}',
  'audit.untimeout': "{actor} removed {target}'s timeout",
  'audit.nick': "{actor} changed {target}'s nickname from {old} to {new}",
  // {roles}, {added}, {removed}: a phrase, `um.audit.roles.*` (auditText.ts)
  'audit.roleAdd': '{actor} gave {target} {roles}',
  'audit.roleRemove': '{actor} removed {roles} from {target}',
  'audit.roleBoth': '{actor} gave {target} {added} and removed {removed}',
  'audit.roleUpdate': '{actor} changed the role {role}',
  'audit.overwrite': '{actor} changed permissions for {target} in {channel}',
  'audit.channelBan': '{actor} banned {target} from {channel} until {time}',
  'audit.channelUnban': "{actor} lifted {target}'s ban from {channel}",
  'audit.move': '{actor} moved {target} to {channel}',
  'audit.disconnect': '{actor} disconnected {target} from voice',
  'audit.mute': '{actor} server-muted {target}',
  'audit.msgDelete': '{actor} deleted a message by {target} in {channel}',
  'audit.invite': '{actor} created the invite {code}',
  'audit.ownerTransfer': '{actor} started an ownership transfer to {target}, effective {date}',
  'audit.ownerTransferDone': '{target} is now the owner',
  'audit.restore': '{actor} restored {target}',
  'audit.reason': 'Reason: {reason}',

  // ---- ours
  'um.menu.mod': 'Moderation',
  'um.menu.nick': 'Change nickname',
  'um.menu.nickMine': 'Change my nickname',
  'um.menu.poke': 'Poke…',
  'um.menu.untimeout': 'Remove timeout',
  'um.menu.unmute': 'Server unmute',
  'um.menu.undeafen': 'Server undeafen',
  // a moderator's mute / deafen in one voice channel (2026-10-28)
  'um.menu.channelMute': 'Channel mute',
  'um.menu.channelUnmute': 'Channel unmute',
  'um.menu.channelDeafen': 'Channel deafen',
  'um.menu.channelUndeafen': 'Channel undeafen',
  'um.menu.ban': 'Ban…',
  'um.menu.kick': 'Kick from server…',
  'um.menu.timeout': 'Time out…',
  'um.menu.owner': 'Server owner',
  'um.menu.notInVoice': 'Not in voice',
  // Discord's member Roles submenu, and the profile's roles
  'um.menu.roles': 'Roles',
  'um.roles.add': 'Add a role',
  'um.roles.remove': 'Remove the role {role}',
  // timeout dialog
  'um.timeout.title': 'Time out {name}',
  'um.timeout.body': "They can read, but can't send messages, react, or talk in voice until then.",
  'um.timeout.for': 'For',
  'um.dur.60s': '60 seconds',
  'um.dur.5m': '5 minutes',
  'um.dur.10m': '10 minutes',
  'um.dur.1h': '1 hour',
  'um.dur.1d': '1 day',
  'um.dur.1w': '1 week',
  'um.dur.custom': 'Custom (up to 28 days)',
  'um.dur.customDays': 'Days (1-28)',
  'um.timeout.until': 'Until {time}',
  'um.timeout.done': '{name} is timed out until {time}.',
  'um.timeout.lifted': "{name}'s timeout is over.",
  'um.timeout.badge': 'Timed out until {time}',
  // kick
  'um.kick.title': 'Kick {name}?',
  'um.kick.body': 'They leave {server} and can rejoin with a new invite.',
  'um.kick.done': '{name} was kicked.',
  // ban
  'um.ban.title': 'Ban {name}',
  'um.ban.for': 'Ban for',
  'um.ban.6h': '6 hours',
  'um.ban.7d': '7 days',
  'um.ban.30d': '30 days',
  'um.ban.perm': 'Permanently',
  'um.ban.delete': 'Delete their messages from',
  'um.ban.del.none': "Don't delete",
  'um.ban.del.1h': 'Last hour',
  'um.ban.del.6h': '6 hours',
  'um.ban.del.12h': '12 hours',
  'um.ban.del.24h': '24 hours',
  'um.ban.del.3d': '3 days',
  'um.ban.del.7d': '7 days',
  'um.ban.done': '{name} was banned.',
  'um.ban.unban': 'Unban',
  'um.ban.unbanned': '{name} can join again.',
  'um.bans.title': 'Bans',
  'um.bans.empty': 'Nobody is banned.',
  'um.bans.until': 'Until {time}',
  'um.bans.perm': 'Permanent',
  // voice moderation
  'um.move.title': 'Move {name} to…',
  'um.move.here': 'Already here',
  'um.move.done': '{name} was moved to {channel}.',
  'um.mute.done': '{name} is server-muted.',
  'um.mute.undone': "{name}'s server mute was removed.",
  'um.deaf.done': '{name} is server-deafened.',
  'um.deaf.undone': "{name}'s server deafen was removed.",
  'um.chmute.done': '{name} is muted in {channel}.',
  'um.chmute.undone': "{name}'s mute in {channel} was removed.",
  'um.chdeaf.done': '{name} is deafened in {channel}.',
  'um.chdeaf.undone': "{name}'s deafen in {channel} was removed.",
  // the voice channel's settings: "Muted in this channel" (2026-10-28)
  'um.chmute.title': 'Muted in this channel',
  'um.chmute.help':
    'People a moderator muted or deafened in this channel. It holds only here, and stays until it is lifted, even while they are somewhere else.',
  'um.chmute.empty': 'Nobody is muted or deafened in this channel.',
  'um.chmute.failed': "Couldn't load who is muted in this channel.",
  'um.chmute.muted': 'Muted',
  'um.chmute.deafened': 'Deafened',
  'um.chmute.by': 'by {actor}, {time}',
  'um.chmute.liftMute': 'Lift mute',
  'um.chmute.liftDeaf': 'Lift deafen',
  // in place of a Lift you can't press: the permission it needs here
  'um.chmute.needMute': 'Lifting it needs the Mute members permission here',
  'um.chmute.needDeaf': 'Lifting it needs the Deafen members permission here',
  'um.chmute.where': 'Now in {channel}',
  'um.disconnect.done': '{name} was disconnected from voice.',
  'um.privacy.title': 'Privacy and safety',
  'um.privacy.unknown': "The server didn't send your privacy settings. Reconnect and try again.",
  'um.privacy.dms': 'Direct messages',
  'privacy.dmsFromMembers': 'Allow DMs from server members',
  'privacy.requests': "Message requests from people who aren't friends",
  'um.privacy.requestsSub':
    "On: a first DM from someone who isn't your friend waits in Message Requests. Off: it goes straight to your DMs.",
  'um.privacy.friendsFrom': 'Who can send you friend requests',
  'um.privacy.ff.everyone': 'Everyone',
  'um.privacy.ff.fof': 'Friends of friends',
  'um.privacy.ff.members': 'Server members',
  'um.privacy.pokesGroup': 'Pokes',
  'um.privacy.pokesFrom': 'Pokes from',
  'um.privacy.pokes.everyone': 'Everyone in the server',
  'um.privacy.friends': 'Friends',
  'um.privacy.nobody': 'Nobody',
  // a moderator's server mute and deafen, told apart from your own (2026-10-28)
  'um.voice.serverMuted': 'Server muted by a moderator',
  'um.voice.serverDeafened': 'Server deafened by a moderator',
  'um.voice.serverBoth': 'Server muted and deafened by a moderator',
  // ...when you hold Mute / Deafen Members there and can undo it yourself
  // (QA 2026-10-28 follow-up round 3)
  'um.voice.serverMutedSelf': 'Server muted by a moderator. You can undo it yourself.',
  'um.voice.serverDeafenedSelf': 'Server deafened by a moderator. You can undo it yourself.',
  'um.voice.serverBothSelf':
    'Server muted and deafened by a moderator. You can undo both yourself.',
  'um.voice.serverBothSelfMute':
    'Server muted and deafened by a moderator. You can undo the mute yourself.',
  'um.voice.serverBothSelfDeaf':
    'Server muted and deafened by a moderator. You can undo the deafen yourself.',
  'um.voice.serverMutedTipSelf':
    'Server muted by a moderator: nobody hears you. You can undo it yourself: right-click your name and choose Server unmute.',
  'um.voice.serverDeafenedTipSelf':
    "Server deafened by a moderator: you can't speak or hear anyone. You can undo it yourself: right-click your name and choose Server undeafen.",
  'um.voice.tip.srvMuteSelfMicOn':
    'Server muted by a moderator: nobody hears you. You can undo it yourself: right-click your name and choose Server unmute. Your own microphone is on.',
  'um.voice.tip.srvMuteSelfMicOff':
    'Server muted by a moderator: nobody hears you. You can undo it yourself: right-click your name and choose Server unmute. Your own microphone is off too.',
  'um.voice.tip.srvDeafSelfMicOn':
    "Server deafened by a moderator: you can't speak or hear anyone. You can undo it yourself: right-click your name and choose Server undeafen. Your own microphone is on.",
  'um.voice.tip.srvDeafSelfMicOff':
    "Server deafened by a moderator: you can't speak or hear anyone. You can undo it yourself: right-click your name and choose Server undeafen. Your own microphone is off too.",
  'um.voice.tip.srvDeafSelfSoundOn':
    "Server deafened by a moderator: you can't speak or hear anyone. You can undo it yourself: right-click your name and choose Server undeafen. Your own sound is on.",
  'um.voice.tip.srvDeafSelfSoundOff':
    "Server deafened by a moderator: you can't speak or hear anyone. You can undo it yourself: right-click your name and choose Server undeafen. Your own sound is off too.",
  'um.voice.serverMutedTip':
    'Server muted by a moderator: nobody hears you until a moderator unmutes you.',
  // a server deafen is no-speak too (Discord's Deafen Members, QA 2026-10-28 round 3)
  'um.voice.serverDeafenedTip':
    "Server deafened by a moderator: you can't speak or hear anyone until a moderator undeafens you.",
  'um.voice.cantSpeakHere': "You can't speak in this channel.",
  // the flags on a person in voice (voice/flags.ts), each its own words
  'um.voice.flag.serverMute': 'Server muted by a moderator',
  'um.voice.flag.serverDeaf': 'Server deafened by a moderator',
  'um.voice.flag.selfMute': 'Microphone muted',
  'um.voice.flag.selfDeaf': 'Sound muted',
  'um.voice.flag.localMute': 'Muted for you',
  'um.voice.flag.noTalk': "Can't speak in this channel",
  // a narrow row's "+N" chip for the flags it has no room for (VoiceFlagIcons)
  'um.voice.flag.more': '{n} more: {list}',
  // your own mic / sound button while a moderator or the channel blocks it (voice/flags.ts)
  'um.voice.btnBlocked': '{name}. {why}',
  'um.voice.tip.srvMuteMicOn':
    'Server muted by a moderator: nobody hears you until a moderator unmutes you. Your own microphone is on.',
  'um.voice.tip.srvMuteMicOff':
    'Server muted by a moderator: nobody hears you until a moderator unmutes you. Your own microphone is off too.',
  'um.voice.tip.noTalkMicOn': "You can't speak in this channel. Your own microphone is on.",
  'um.voice.tip.noTalkMicOff': "You can't speak in this channel. Your own microphone is off too.",
  'um.voice.tip.srvDeafMicOn':
    "Server deafened by a moderator: you can't speak or hear anyone until a moderator undeafens you. Your own microphone is on.",
  'um.voice.tip.srvDeafMicOff':
    "Server deafened by a moderator: you can't speak or hear anyone until a moderator undeafens you. Your own microphone is off too.",
  'um.voice.tip.srvDeafSoundOn':
    "Server deafened by a moderator: you can't speak or hear anyone until a moderator undeafens you. Your own sound is on.",
  'um.voice.tip.srvDeafSoundOff':
    "Server deafened by a moderator: you can't speak or hear anyone until a moderator undeafens you. Your own sound is off too.",
  // a moderator's channel mute / deafen, in the channel you are in (2026-10-28)
  'um.voice.flag.channelMute': 'Muted in this channel by a moderator',
  'um.voice.flag.channelDeaf': 'Deafened in this channel by a moderator',
  'um.voice.channelMuted': 'Muted in this channel by a moderator',
  'um.voice.channelDeafened': 'Deafened in this channel by a moderator',
  'um.voice.channelBoth': 'Muted and deafened in this channel by a moderator',
  'um.voice.channelMutedTip':
    'Muted in this channel by a moderator: nobody here hears you until a moderator unmutes you. Other channels hear you as usual.',
  'um.voice.channelDeafenedTip':
    "Deafened in this channel by a moderator: you can't speak or hear anyone here until a moderator undeafens you. Other channels are as usual.",
  'um.voice.tip.chMuteMicOn':
    'Muted in this channel by a moderator: nobody here hears you until a moderator unmutes you. Other channels hear you as usual. Your own microphone is on.',
  'um.voice.tip.chMuteMicOff':
    'Muted in this channel by a moderator: nobody here hears you until a moderator unmutes you. Other channels hear you as usual. Your own microphone is off too.',
  'um.voice.tip.chDeafMicOn':
    "Deafened in this channel by a moderator: you can't speak or hear anyone here until a moderator undeafens you. Your own microphone is on.",
  'um.voice.tip.chDeafMicOff':
    "Deafened in this channel by a moderator: you can't speak or hear anyone here until a moderator undeafens you. Your own microphone is off too.",
  'um.voice.tip.chDeafSoundOn':
    "Deafened in this channel by a moderator: you can't speak or hear anyone here until a moderator undeafens you. Your own sound is on.",
  'um.voice.tip.chDeafSoundOff':
    "Deafened in this channel by a moderator: you can't speak or hear anyone here until a moderator undeafens you. Your own sound is off too.",
  // ...when you hold Mute / Deafen Members there and can lift it yourself
  'um.voice.channelMutedSelf': 'Muted in this channel by a moderator. You can undo it yourself.',
  'um.voice.channelDeafenedSelf':
    'Deafened in this channel by a moderator. You can undo it yourself.',
  'um.voice.channelBothSelf':
    'Muted and deafened in this channel by a moderator. You can undo both yourself.',
  'um.voice.channelBothSelfMute':
    'Muted and deafened in this channel by a moderator. You can undo the mute yourself.',
  'um.voice.channelBothSelfDeaf':
    'Muted and deafened in this channel by a moderator. You can undo the deafen yourself.',
  'um.voice.channelMutedTipSelf':
    'Muted in this channel by a moderator: nobody here hears you. You can undo it yourself: right-click your name and choose Channel unmute.',
  'um.voice.channelDeafenedTipSelf':
    "Deafened in this channel by a moderator: you can't speak or hear anyone here. You can undo it yourself: right-click your name and choose Channel undeafen.",
  'um.voice.tip.chMuteSelfMicOn':
    'Muted in this channel by a moderator: nobody here hears you. You can undo it yourself: right-click your name and choose Channel unmute. Your own microphone is on.',
  'um.voice.tip.chMuteSelfMicOff':
    'Muted in this channel by a moderator: nobody here hears you. You can undo it yourself: right-click your name and choose Channel unmute. Your own microphone is off too.',
  'um.voice.tip.chDeafSelfMicOn':
    "Deafened in this channel by a moderator: you can't speak or hear anyone here. You can undo it yourself: right-click your name and choose Channel undeafen. Your own microphone is on.",
  'um.voice.tip.chDeafSelfMicOff':
    "Deafened in this channel by a moderator: you can't speak or hear anyone here. You can undo it yourself: right-click your name and choose Channel undeafen. Your own microphone is off too.",
  'um.voice.tip.chDeafSelfSoundOn':
    "Deafened in this channel by a moderator: you can't speak or hear anyone here. You can undo it yourself: right-click your name and choose Channel undeafen. Your own sound is on.",
  'um.voice.tip.chDeafSelfSoundOff':
    "Deafened in this channel by a moderator: you can't speak or hear anyone here. You can undo it yourself: right-click your name and choose Channel undeafen. Your own sound is off too.",
  // nicknames
  'um.nick.title': 'Nickname for {name}',
  'um.nick.titleMine': 'Your nickname in {server}',
  'um.nick.label': 'Nickname',
  'um.nick.help':
    'Only in this server. Leave it empty to use the display name. Up to 32 characters.',
  'um.nick.reset': 'Reset',
  'um.nick.saved': 'Nickname saved.',
  'um.nick.cleared': 'Nickname cleared.',
  // pokes
  'um.poke.couldNot': "Couldn't poke {name}.",
  'um.poke.disallowed': "Couldn't poke {name}.",
  // ignore
  'um.ignore.on': "You're ignoring {name}.",
  'um.ignore.off': "You're no longer ignoring {name}.",
  'um.ignore.hide': 'Hide',
  'um.ignore.badge': 'Ignored',
  'um.prof.inVoice': 'In {channel}',
  // message requests
  'um.requests.from': 'From {name}',
  'um.requests.accepted': 'Accepted. {name} is in your messages now.',
  'um.requests.dismissed': 'Dismissed.',
  'um.requests.loadFailed': "Couldn't load message requests.",
  'um.requests.link': '[link hidden]',
  'um.requests.media': { one: '[{n} attachment hidden]', other: '[{n} attachments hidden]' },
  'um.requests.reportSent': 'Thanks. We sent the report to {brand} Safety.',
  // search
  'um.search.label': 'Search messages',
  'um.search.ph': 'Search',
  'um.search.help': 'Filters: from: in: has:image has:link',
  'um.search.from': 'from: a person',
  'um.search.in': 'in: a channel',
  'um.search.hasImage': 'has: image',
  'um.search.hasLink': 'has: link',
  'um.search.results': { one: '{n} result', other: '{n} results' },
  'um.search.none': 'No messages match.',
  'um.search.failed': "Search didn't work. Try again.",
  'um.search.unknownFrom': 'No one called "{name}" here.',
  'um.search.unknownIn': 'No channel called "{name}" here.',
  'um.search.unknownHas': 'has: takes image or link.',
  'um.search.more': 'Show more',
  'um.search.jumpFailed': "Couldn't find that message. It may have been deleted.",
  'um.search.in.channel': 'in #{channel}',
  // a voice or stage channel's own chat: no '#' (Discord)
  'um.search.in.voice': 'in {channel}',
  // audit log
  'um.audit.title': 'Audit log',
  'um.audit.empty': 'Nothing here yet.',
  'um.audit.allActions': 'All actions',
  'um.audit.filterAction': 'Action',
  'um.audit.filterWho': 'Done by',
  'um.audit.allPeople': 'Everyone',
  'um.audit.more': 'Older entries',
  'um.audit.failed': "Couldn't load the audit log.",
  'um.audit.live': 'Live',
  'um.audit.generic': '{actor}: {action}',
  'um.audit.channelCreate': '{actor} created the channel {target}',
  'um.audit.channelUpdate': '{actor} changed the channel {target}',
  'um.audit.channelDelete': '{actor} deleted the channel {target}',
  'um.audit.roleCreate': '{actor} created the role {role}',
  'um.audit.roleDelete': '{actor} deleted the role {role}',
  'um.audit.inviteDelete': '{actor} revoked the invite {code}',
  'um.audit.memberUpdate': '{actor} changed {target}',
  'um.audit.guildUpdate': '{actor} changed the server settings',
  'um.audit.ownerTransferCancel': '{actor} cancelled the ownership transfer',
  'um.audit.deaf': '{actor} server-deafened {target}',
  'um.audit.unmute': "{actor} removed {target}'s server mute",
  'um.audit.undeaf': "{actor} removed {target}'s server deafen",
  'um.audit.channelMute': '{actor} channel-muted {target} in {channel}',
  'um.audit.channelUnmute': "{actor} removed {target}'s channel mute in {channel}",
  'um.audit.channelDeaf': '{actor} channel-deafened {target} in {channel}',
  'um.audit.channelUndeaf': "{actor} removed {target}'s channel deafen in {channel}",
  // a channel the line can't name (not one you can see, and no stored name)
  'um.audit.aChannel': 'a channel',
  'um.audit.pin': '{actor} pinned a message by {target} in {channel}',
  'um.audit.unpin': '{actor} unpinned a message by {target} in {channel}',
  'um.audit.channelBanPerm': '{actor} banned {target} from {channel} permanently',
  'um.audit.passwordOn': '{actor} set a password on {channel}',
  'um.audit.passwordOff': '{actor} removed the password from {channel}',
  'um.audit.someone': 'someone',
  // the roles of a role grant or removal: "the role Mod", "the roles Mod and
  // VIP" (n counts a deleted one too), "a deleted role", "2 deleted roles"
  'um.audit.roles.named': { one: 'the role {roles}', other: 'the roles {roles}' },
  'um.audit.roles.deleted': { one: 'a deleted role', other: '{n} deleted roles' },
  'um.audit.system': 'System',
  // an entry's source; legacy "comms" entries read as the app's too (auditSourceKey)
  'um.audit.src.app': 'via the app',
  'um.audit.src.system': 'via System',
  // why a one-time data fix changed something (auditReason), in Hub's words
  'um.audit.fix.noSpeak': "{role} can't speak in this channel (Speak is denied)",
  'um.audit.fix.rename': 'A starter voice channel got its new name',
  'um.audit.fix.remove': 'Starter channels that are no longer used were removed',
  'um.audit.fix.english': 'A starter text is now in English, like the rest of the app',
  'um.audit.fix.other': 'An automatic update',
  'um.audit.f.kick': 'Kicks',
  'um.audit.f.ban': 'Bans',
  'um.audit.f.unban': 'Unbans',
  'um.audit.f.member': 'Member changes',
  'um.audit.f.roles': 'Member roles',
  'um.audit.f.move': 'Voice moves',
  'um.audit.f.disconnect': 'Voice disconnects',
  'um.audit.f.channel': 'Channel changes',
  'um.audit.f.role': 'Role changes',
  'um.audit.f.invite': 'Invites',
  'um.audit.f.message': 'Message deletes',
  // invites
  'um.invites.title': 'Invites',
  'um.invites.create': 'Create invite',
  'um.invites.channel': 'Invite to',
  'um.invites.expire': 'Expire after',
  'um.invites.maxUses': 'Max uses',
  'um.invites.age.30m': '30 minutes',
  'um.invites.age.1h': '1 hour',
  'um.invites.age.6h': '6 hours',
  'um.invites.age.12h': '12 hours',
  'um.invites.age.1d': '1 day',
  'um.invites.age.7d': '7 days',
  'um.invites.age.never': 'Never',
  'um.invites.uses.none': 'No limit',
  'um.invites.empty': 'No active invites.',
  'um.invites.uses': { one: '{uses} of {n} use', other: '{uses} of {n} uses' },
  'um.invites.usesNoMax': { one: '{n} use', other: '{n} uses' },
  'um.invites.expires': 'Expires {time}',
  'um.invites.never': "Doesn't expire",
  'um.invites.by': 'Made by {name}',
  'um.invites.revoke': 'Revoke',
  'um.invites.revokeConfirm': 'Revoke {code}? The link stops working right away.',
  'um.invites.revoked': 'Invite revoked.',
  'um.invites.created': 'Invite created and copied.',
  'um.invites.createdNoCopy': 'Invite created.',
  'um.invites.failed': "Couldn't load invites.",
  'um.invites.mine': 'Only your own invites are listed. Manage Server shows everyone’s.',
  // ownership transfer
  'um.owner.title': 'Ownership',
  'um.owner.current': 'Owner: {name}',
  'um.owner.pick': 'New owner',
  'um.owner.pickPh': 'Choose a member',
  'um.owner.start': 'Transfer ownership',
  'um.owner.done': '{name} is now the owner of {server}.',
  'um.owner.onlyOwner': 'Only the owner can transfer the server.',
  // recently deleted
  'um.deleted.empty': 'Nothing was deleted in the last 7 days.',
  'um.deleted.channel': 'Channel',
  'um.deleted.role': 'Role',
  'um.deleted.restored': '{name} is back.',
  'um.deleted.failed': "Couldn't load recently deleted items.",
  // step-up and brake
  'um.stepUp.title': 'Confirm with two-step',
  'um.stepUp.titlePassword': "Confirm it's you",
  'um.stepUp.code': 'Two-step code',
  'um.stepUp.password': 'Password',
  'um.stepUp.useRecovery': 'Use a recovery code instead',
  'um.stepUp.continue': 'Continue',
  'um.stepUp.passwordInstead':
    "Two-step isn't on for your account, so your password confirms it instead.",
  'um.stepUp.wrong': "That didn't work. Check it and try again.",
  'um.stepUp.wait': {
    one: 'Too many tries. Wait {n} second, then try again.',
    other: 'Too many tries. Wait {n} seconds, then try again.',
  },
  'um.stepUp.waitMin': {
    one: 'Too many tries. Wait {n} minute, then try again.',
    other: 'Too many tries. Wait {n} minutes, then try again.',
  },
  'um.destructive.title': 'Delete {name}?',
  'um.destructive.restore': 'It can be restored from Recently deleted for 7 days.',
  // security alert
  'um.alert.brake': '{actor} made many moderation actions in a short time.',
  'um.alert.channelDelete':
    '{actor} deleted a channel. It can be restored for 7 days in Server settings › Recently deleted.',
  'um.alert.roleDelete':
    '{actor} deleted a role. It can be restored for 7 days in Server settings › Recently deleted.',
  'um.alert.dangerousGrant': '{actor} gave out Administrator or a Manage permission.',
  'um.alert.transferDone': '{target} is now the owner of the server.',
  // recovery screens
  'um.recovery.newPassword': 'New password',
  'um.recovery.codeLabel': 'Recovery code',
  'um.recovery.send': 'Continue',
  'um.recovery.waitingDevice':
    'Open {brand} on a device where you are signed in, and approve the request there. This page continues by itself.',
  'um.recovery.stillWaiting': 'Waiting for approval…',
  'um.recovery.held': 'Your request is on hold until {date} at {time}.',
  'um.recovery.cancelled': 'The request is cancelled.',
  'um.recovery.expired': 'This request ended. Start again if you still need to.',
  'um.recovery.done': "You're back in. We signed out every other device.",
  'um.recovery.cancelElsewhere': "To cancel it, use a device where you're still signed in.",
  'um.recovery.forget': 'Stop waiting on this device',
  'um.recovery.lostLink': "I don't have any of these",
  'um.recovery.method.code': 'a recovery code',
  'um.recovery.method.device': 'another device',
  'um.recovery.approve': 'It was me: approve',
  'um.recovery.approved': 'Approved. That device can finish signing in now.',
  'um.recovery.cancelDone': 'Cancelled. Nobody got in.',
  'um.recovery.where': 'From {device}, near {city}',
  'um.recovery.whereDevice': 'From {device}',
  // new account
  // Settings > Server
  'um.server.title': 'Server',
  'um.server.change': 'Change',
  'um.server.set': 'Set the server address',
  'um.server.needed': 'This app has no server address yet. Set it before signing in.',
  'um.server.origin': 'Server address',
  'um.server.originHelp':
    'Where this app connects, for example https://chat.example.ir. Use https; plain http works only for this computer (127.0.0.1).',
  'um.server.current': 'Connected to {host}',
  'um.server.address': 'Server: {host}',
  'um.server.unreachable': "Can't reach {host}",
  'um.server.sameOrigin': 'This app’s own address (development)',
  'um.server.save': 'Save and reconnect',
  'um.server.reset': 'Use the built-in address',
  'um.server.invalid':
    'That address needs https:// and a host name, with no path. Plain http works only for 127.0.0.1.',
  'um.server.fromEnv':
    'Set by NEOSOCIAL_SERVER_ORIGIN when the app was started, so it can’t be changed here.',
  'um.server.fromSettings': 'Saved in this app’s settings.',
  'um.server.fromBuild': 'Built into this copy of the app.',
  'um.server.saved': 'Saved. Reconnecting…',
  'um.server.webOnly': 'In the browser, the server is the site you opened: {host}.',
  'um.server.signOutFirst': 'Changing the server signs you out here.',
  'um.server.oldShell': 'This version of the desktop app can’t change its server. Update it first.',
  // volume
  'um.vol.boost': 'Above 100% boosts their voice.',
  'um.vol.gone': "{name} isn't in your voice channel now.",
  // drag reorder
  'um.drag.hint': 'Drag to reorder',
  'um.drag.saved': 'New order saved.',
  'um.drag.moveUp': 'Move up',
  'um.drag.moveDown': 'Move down',
  'um.drag.moveToCategory': 'Move to category',
  'um.drag.reorder': 'Reorder',
  'um.drag.reorderDone': 'Done',
  'um.drag.reorderHint':
    'Move channels and categories with the buttons. Each move is saved at once.',
  // admin tabs
  'um.admin.invites': 'Invites',
  'um.admin.audit': 'Audit log',
  'um.admin.bans': 'Bans',
  'um.admin.deleted': 'Recently deleted',
  'um.admin.owner': 'Ownership',
  'um.admin.serverMenu': 'Server settings',
  // errors
  'ns.err.100002':
    'This server requires two-step verification for moderators. Turn it on to continue.',
  'ns.err.40007': 'That person is banned from this server.',
  'ns.err.100026': 'A recovery is already running for this account.',
  'ns.err.100027': 'A recovery was cancelled recently. Try again tomorrow.',
  'ns.err.100028': 'This recovery is not ready yet.',
  'ns.err.100029': "Couldn't poke.",
  'ns.err.100030': 'An ownership transfer is already waiting. Cancel it first.',
  'ns.err.100031': 'This needs a fresh two-step code (or your password).',
  'ns.err.100032': "That's many actions in a short time. Confirm it's you to continue.",
  'ns.err.100033': 'The new owner needs two-step verification turned on.',
  // poke rules (2026-10-29): poke is an in-server feature
  'ns.err.100045': "You're invisible. Go online to poke someone.",
  'ns.err.100046': 'Join a voice channel to poke someone.',
  'ns.err.100047': "They're offline.",
  'ns.err.100048': "Their role doesn't let you poke them.",
  'ns.err.10006': 'This invite is invalid or has expired.',
  'ns.err.10007': "That person isn't in this server any more.",
  'ns.err.10026': "That person isn't banned.",
  'ns.err.30016': 'This server has reached its invite limit.',
  'ns.err.100004':
    "Your account is limited for now. You can message friends, but you can't add friends, join servers or message new people.",
};
